import {useContext, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {MISSING_ID} from '@common/utils'
import {
  NotFoundApiRequestError,
  type PreAppointmentQuestionnaireResponse,
} from '@common/models'

import {PreQuestionnaireRenderer} from '~/components/bookings/PreQuestionnaireRenderer'
import {
  isRequiredPreqComplete,
  toPreQuestionnaireLiteralAnswers,
  type PreQuestionnaireAnswers,
} from '~/components/bookings/preQuestionnaireAnswers'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {SideModal} from '~/components/general/modal/SideModal'
import {Loading} from '~/components/general/loading/Loading'
import {Button} from '~/components/general/button/Button'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {
  useGetPreAppointmentQuestionnaire,
  useGetPreAppointmentQuestionnaireDefinition,
  useSavePreAppointmentQuestionnaire,
} from '~/api'

type PreAppointmentSurveyModalProps = {
  isVisible: boolean
  onClose: () => void
  facilityId: string
  appointmentId: string
}

export const PreAppointmentSurveyModal = ({
  isVisible,
  onClose,
  facilityId,
  appointmentId,
}: PreAppointmentSurveyModalProps) => {
  const {t} = useTranslation<string>()
  const {addNotification} = useContext(NotificationContext)
  const [preqAnswers, setPreqAnswers] = useState<PreQuestionnaireAnswers>({})

  const questionnaireParams = {
    facilityId: facilityId ?? MISSING_ID,
    appointmentId: appointmentId ?? MISSING_ID,
  }

  const {data, isInitialLoading, isError, error} =
    useGetPreAppointmentQuestionnaire(questionnaireParams)

  const isNotFoundError = isError && error instanceof NotFoundApiRequestError

  const {
    data: definition,
    isInitialLoading: isDefinitionLoading,
    isError: isDefinitionError,
    error: definitionError,
  } = useGetPreAppointmentQuestionnaireDefinition(questionnaireParams, {
    enabled: isNotFoundError,
  })

  const isDefinitionNotFound =
    isDefinitionError && definitionError instanceof NotFoundApiRequestError

  const {
    mutate,
    isPending,
    data: savedQuestionnaire,
  } = useSavePreAppointmentQuestionnaire(questionnaireParams)

  const questionnaire = data ?? savedQuestionnaire

  const canSave =
    !!definition &&
    isRequiredPreqComplete(definition, preqAnswers) &&
    !isPending &&
    !savedQuestionnaire

  const handleSave = () => {
    if (!definition || !canSave) return
    mutate(
      {answers: toPreQuestionnaireLiteralAnswers(definition, preqAnswers)},
      {
        onSuccess: () => {
          addNotification(
            t('Pre-appointment survey saved'),
            NotificationType.Success,
          )
        },
      },
    )
  }

  const renderReadOnly = (
    questionnaireResponse: PreAppointmentQuestionnaireResponse,
  ) => {
    return (
      <Container flex direction="column" spacing={4}>
        {questionnaireResponse.questions.map((questionAnswer, index) => {
          const hasAnswer = !!questionAnswer.answer

          return (
            <Container
              key={`question-${index}`}
              flex
              direction="column"
              spacing={1}>
              <Typography weight="medium" size="md">
                {questionAnswer.question}
              </Typography>
              {hasAnswer ? (
                <Typography>{questionAnswer.answer}</Typography>
              ) : (
                <Typography color="grey">{t('No answer provided')}</Typography>
              )}
            </Container>
          )
        })}
      </Container>
    )
  }

  const renderForm = () => {
    if (isDefinitionLoading) {
      return <Loading />
    }

    if (isDefinitionError && !isDefinitionNotFound) {
      return (
        <Container flex direction="column" spacing={3}>
          <Typography>
            {t('An error occurred while loading the survey')}
          </Typography>
        </Container>
      )
    }

    if (isDefinitionNotFound || !definition) {
      return (
        <Container flex direction="column" spacing={3}>
          <Typography>
            {t('No Pre-Appointment Survey available for this appointment')}
          </Typography>
        </Container>
      )
    }

    return (
      <Container flex direction="column" spacing={4}>
        <PreQuestionnaireRenderer
          questionnaire={definition}
          answers={preqAnswers}
          onChange={(questionId, answer) =>
            setPreqAnswers((current) => ({
              ...current,
              [questionId]: answer,
            }))
          }
        />
        <Container flex justify="flex-end">
          <Button onClick={handleSave} disabled={!canSave}>
            {t('Save Pre-Appointment Survey')}
          </Button>
        </Container>
      </Container>
    )
  }

  const renderContent = () => {
    if (isInitialLoading) {
      return <Loading />
    }

    if (questionnaire) {
      return renderReadOnly(questionnaire)
    }

    if (isNotFoundError) {
      return renderForm()
    }

    if (isError) {
      return (
        <Container flex direction="column" spacing={3}>
          <Typography>
            {t('An error occurred while loading the survey')}
          </Typography>
        </Container>
      )
    }

    return null
  }

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={
        <Container>
          <Typography size="lg" weight="medium">
            {questionnaire?.questionnaireTitle ||
              definition?.title ||
              t('Pre-Appointment Survey')}
          </Typography>
        </Container>
      }>
      {renderContent()}
    </SideModal>
  )
}
